<div class="manage-project-container document-wrapper" [ngStyle]="{'max-height': scrHeight}">
  <div class="main-card">
    <!-- Header Section -->
    <div class="header-section">
      <div class="header-content">
        <div class="header-text">
          <h1>Manage Projects</h1>
          <p>View and manage all your projects with their naming conventions and color codes.</p>
        </div>
        <div class="header-actions">
          <button class="btn-add-project" (click)="openAddProjectDialog()">
            <mat-icon>add</mat-icon>
            Add Project
          </button>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="stats-section">
      <div class="stat-card active">
        <div class="stat-header">
          <div class="stat-icon active">
            <mat-icon>folder</mat-icon>
          </div>
          <div>
            <div class="stat-value">{{ getActiveProjects().length }}</div>
            <div class="stat-label">Active Projects</div>
          </div>
        </div>
      </div>

      <div class="stat-card deleted">
        <div class="stat-header">
          <div class="stat-icon deleted">
            <mat-icon>archive</mat-icon>
          </div>
          <div>
            <div class="stat-value">{{ getDeletedProjects().length }}</div>
            <div class="stat-label">Archived Projects</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabs and Search Section -->
    <div class="tabs-search-section">
      <div class="tabs-container">
        <button 
          class="tab-button" 
          [class.active]="activeTab === 'active'"
          (click)="activeTab = 'active'; filterProjects()">
          <span class="d-none d-sm-inline">Active List</span>
          <span class="d-inline d-sm-none">Active</span>
          <span class="tab-badge">{{ getActiveProjects().length }}</span>
        </button>
        <button 
          class="tab-button" 
          [class.active]="activeTab === 'deleted'"
          (click)="activeTab = 'deleted'; filterProjects()">
          <span class="d-none d-sm-inline">Archived List</span>
          <span class="d-inline d-sm-none">Archived</span>
          <span class="tab-badge">{{ getDeletedProjects().length }}</span>
        </button>
      </div>

      <div class="search-container">
        <mat-icon>search</mat-icon>
        <input 
          class="search-input"
          type="text" 
          [placeholder]="activeTab === 'deleted' ? 'Search archived projects...' : 'Search active projects...'"
          [(ngModel)]="searchTerm"
          (input)="onSearch()">
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div class="modal-backdrop show" *ngIf="showDeleteConfirmModal" (click)="cancelDelete()"></div>
    <div class="modal d-flex show" *ngIf="showDeleteConfirmModal" (click)="$event.stopPropagation()">
      <div class="modal-dialog modal-dialog-centered modal-dialog-custom m-auto">
        <div class="modal-content">
          <div class="modal-header border-0">
            <h5 class="modal-title fs-6 fw-bold m-0 d-flex align-items-center gap-2 text-danger">
              <mat-icon style="font-size: 26px;">error_outline</mat-icon><span>Confirm Deletion</span>
            </h5>
            <button type="button" class="close-btn" (click)="cancelDelete()" title="Cancel">✕</button>
          </div>

          <hr class="divider" />

          <div class="modal-body">
            <p class="text-muted small mb-3">Are you sure you want to archive "<strong>{{deleteConfirmTitle}}</strong>"?</p>
            <div class="delete-info-box">
              <div class="small text-muted">Note: Project will be moved to Archived List</div>
              <div class="small text-muted">You can restore this project from the Archived List if needed.</div>
            </div>
          </div>

          <hr class="divider" />

          <div class="modal-footer border-0">
            <button type="button" class="btn btn-outline-secondary btn-cancel" (click)="cancelDelete()">Cancel</button>
            <button type="button" class="btn btn-danger d-inline-flex align-items-center gap-1" (click)="confirmDelete()"><mat-icon>archive</mat-icon> Archive</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Projects List - Grouped by Color (Expansion panels) -->
    <div class="projects-list-section">
      <mat-accordion multi>
        <mat-expansion-panel 
          *ngFor="let group of groupedProjects" 
          class="color-group"
          [expanded]="group.isExpanded"
          (opened)="group.isExpanded = true"
          (closed)="group.isExpanded = false">
          <mat-expansion-panel-header class="color-group-header">
            <div class="color-group-indicator">
              <div class="color-dot" [style.background-color]="group.colorCode"></div>
              <span class="color-name">{{ group.colorName }}</span>
              <span class="project-count">{{ group.projects.length }}</span>
            </div>
          </mat-expansion-panel-header>



          <!-- Projects in Group -->
          <div class="group-projects" [@groupListAnimation]="group.isExpanded ? 'expanded' : 'collapsed'">
            <div 
              *ngFor="let project of group.projects" 
              class="project-item"
              [class.deleted]="project.isDeleted">
              <div class="project-header">
                <div class="project-info">
                  <div class="project-color" [style.background-color]="project.colorCode"></div>
                  <div class="project-details">
                    <div class="project-name">
                      <span class="fw-bold">{{ project.projectName }}</span>
                      <span class="naming-badge custom" *ngIf="project.namingMode === 'custom'">
                        {{ project.namingMode }}
                      </span>
                    </div>
                    <div class="project-meta">
                      <!-- Show metadata only for standard naming mode -->
                      <span class="meta-item" *ngIf="project.namingMode === 'standard' && project.modelYear && project.modelYear !== 'N/A'">
                        <span class="meta-label">Model Year:</span> {{ project.modelYear }}
                      </span>
                      <span class="meta-item" *ngIf="project.namingMode === 'standard' && project.modelCode && project.modelCode !== 'N/A'">
                        <span class="meta-label">Model Code:</span> {{ project.modelCode }}
                      </span>
                      <span class="meta-item" *ngIf="project.namingMode === 'standard' && project.projectCode && project.projectCode !== 'N/A'">
                        <span class="meta-label">Project Code:</span> {{ project.projectCode }}
                      </span>
                      <span class="meta-item">
                        <span class="meta-label">Created:</span> {{ (project.dateCreated | date:'MM/dd/yyyy hh:mm a') }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="project-actions">
                  <button class="btn-action btn-edit" (click)="editProject(project)" *ngIf="!project.isDeleted">
                    <mat-icon>edit</mat-icon>
                    Edit
                  </button>
                  <button class="btn-action btn-restore" (click)="restoreProject(project.id)" *ngIf="project.isDeleted">
                    <mat-icon>restore</mat-icon>
                    Restore
                  </button>
                  <button class="btn-action btn-delete" (click)="deleteProject(project.id)" *ngIf="!project.isDeleted">
                    <mat-icon>archive</mat-icon>
                    Archive
                  </button>
                </div>
              </div>
            </div>
          </div>
        </mat-expansion-panel>
      </mat-accordion>

      <div *ngIf="groupedProjects.length === 0" class="no-projects">
        <mat-icon>folder_off</mat-icon>
        <p>No projects found</p>
      </div>
    </div>
  </div>

  <app-tutorial-video-widget
    screenId="manage-project"
    title="Manage Projects walkthrough"
    teaserDescription="Watch a quick walkthrough of this screen."
    storageKey="manage-project-tutorial-seen"
    teaserPosition="bottom-right"
    launcherPosition="bottom-right"
    [autoPlaySequence]="adminTutorialSequence"
    [allowLauncherDismiss]="true">
  </app-tutorial-video-widget>
</div>
